import { saveAs } from 'file-saver';
import moment from 'moment';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export const generateServiceDeskReport = async (tickets, filters = {}) => {
  // Gerar nome do arquivo seguindo o padrão: NomePrefeitura_TipoRelatorio_dia.mes.ano_horaminuto
  const orgInfo = getOrganizationInfo(tickets);
  // Converter nome da organização para CamelCase (ex.: "Prefeitura de Catanduva" -> "PrefeituraDeCatanduva")
  const orgName = orgInfo.name
    ? orgInfo.name
        .split(' ')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('')
    : 'Organizacao';
  const date = moment().format('DD.MM.YYYY_HHmmss');
  const fileName = `${orgName}_ServiceDesk_${date}.pdf`;

  // Criar PDF
  const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape para mais espaço
  
  // Configurações
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 12; // margem menor para ganhar espaço
  const contentWidth = pageWidth - (margin * 2);
  
  let yPosition = margin;
  const gutter = 6;
  const columnWidth = contentWidth; // layout em uma coluna (landscape já dá espaço)

  const ensureSpace = (required = 8) => {
    const bottom = pageHeight - 26; // reservar menos espaço para rodapé
    if (yPosition + required > bottom) {
      pdf.addPage();
      yPosition = margin;
    }
  };

  const writeTitle = (title) => {
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    ensureSpace(8);
    pdf.text(title, margin, yPosition);
    yPosition += 6;
  };

  const writeBulletList = (items = [], fontSize = 9, lineHeight = 4) => {
    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', 'normal');
    items.forEach((item) => {
      const text = `• ${String(item)}`;
      const lines = pdf.splitTextToSize(text, columnWidth - 4);
      lines.forEach((line) => {
        ensureSpace(lineHeight);
        pdf.text(line, margin + 2, yPosition);
        yPosition += Math.max(3.5, lineHeight - 0.5);
      });
    });
  };

  const writeKeyValueList = (pairs = [], fontSize = 9, lineHeight = 4) => {
    const items = pairs.map((p) => `${p.label} ${p.value}`);
    writeBulletList(items, fontSize, lineHeight);
  };
  
  // Cabeçalho com logo da organização
  // orgInfo já foi declarado acima
  
  // Desenhar borda do cabeçalho (sem preenchimento)
  pdf.setDrawColor(200, 200, 200); // Cinza claro
  pdf.setLineWidth(0.5);
  pdf.roundedRect(margin, yPosition - 4, contentWidth, 20, 2, 2, 'S');
  
  // Logo da organização (se disponível)
  if (orgInfo.logoUrl && orgInfo.logoUrl.startsWith('http')) {
    try {
      // Adicionar logo no canto esquerdo
      pdf.addImage(orgInfo.logoUrl, 'PNG', margin + 8, yPosition, 18, 15);
    } catch (error) {
      console.warn('Erro ao carregar logo:', error);
    }
  }
  
  // Título do relatório
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('RELATÓRIO DE CHAMADOS - SERVICE DESK', pageWidth / 2, yPosition + 6, { align: 'center' });
  
  // Nome da organização
  if (orgInfo.name) {
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(orgInfo.name, pageWidth / 2, yPosition + 12, { align: 'center' });
  }
  
  yPosition += 24;
  
  // Informações de geração (à esquerda, sem paginação no cabeçalho)
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Gerado em: ${moment().format('DD/MM/YYYY HH:mm:ss')}`, margin, yPosition);
  yPosition += 8;

  // Linha separadora
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.5);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 6;
  
  // Informações dos filtros (bullets)
  writeTitle('FILTROS APLICADOS:');
  const filterInfo = generateFilterInfo(filters);
  const filterLines = filterInfo.split('\n').filter(line => line.trim());
  writeBulletList(filterLines, 9, 3.5);
  
  yPosition += 6;
  
  // Lista detalhada de tickets
  if (tickets.length === 0) {
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'italic');
    pdf.text('Nenhum chamado encontrado com os filtros aplicados.', margin, yPosition);
  } else {
    tickets.forEach((ticket, index) => {
      // Verificar se precisa de nova página
      if (yPosition > pageHeight - 54) {
        pdf.addPage();
        yPosition = margin;
      }
      
      // Cabeçalho do ticket
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPosition - 4, contentWidth, 10, 'F');
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`CHAMADO #${ticket.id}`, margin + 5, yPosition + 3);
      yPosition += 12;

      // Status, prioridade e estágio em bullets
      writeKeyValueList([
        { label: 'Status:', value: ticket.status || 'N/A' },
        { label: 'Prioridade:', value: ticket.prioridade || 'N/A' },
        { label: 'Estágio:', value: ticket.estagio || 'N/A' },
      ], 9, 3.5);
      yPosition += 2;
      
      // Informações do ticket
      writeTitle('RESUMO:');
      writeBulletList([ticket.resumo || 'Sem resumo'], 9, 3.5);
      yPosition += 3;
      
      // Descrição
      if (ticket.descricao) {
        writeTitle('DESCRIÇÃO:');
        writeBulletList([ticket.descricao], 9, 3.5);
        yPosition += 3;
      }
      
      // Informações do usuário
      writeTitle('INFORMAÇÕES DO USUÁRIO:');
      writeKeyValueList([
        { label: 'Nome:', value: ticket.usuario?.name || 'N/A' },
        { label: 'Telefone:', value: ticket.telefone || 'N/A' },
        { label: 'Organização:', value: ticket.org?.name || 'N/A' },
        { label: 'Filial:', value: ticket.branch?.name || 'N/A' },
        { label: 'Unidade:', value: ticket.unit?.name || 'N/A' },
        { label: 'Departamento:', value: ticket.department?.name || 'N/A' },
      ], 9, 3.5);
      yPosition += 2;
      
      // Datas
      writeKeyValueList([
        { label: 'Data de Criação:', value: ticket.createdAt ? moment(ticket.createdAt).format('DD/MM/YYYY HH:mm') : 'N/A' },
        { label: 'Última Atualização:', value: ticket.updatedAt ? moment(ticket.updatedAt).format('DD/MM/YYYY HH:mm') : 'N/A' },
      ], 9, 4);
      yPosition += 2;
      
      // Observação
      if (ticket.observacao) {
        writeTitle('OBSERVAÇÃO:');
        writeBulletList([ticket.observacao], 9, 3.5);
        yPosition += 3;
      }
    });
  }
  
  // Rodapé
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    
    // Linha separadora do rodapé
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);
    
    // Logo da Solis no canto direito
    try {
      // Usar a URL da logo da Solis (mesma usada no orgReport)
      const solisLogoUrl = 'https://oxaxpvpxjaojruodsvbq.supabase.co/storage/v1/object/sign/adaptep/logo_solis_invert.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV85NDA2YWQ3MC01ZDM2LTRjZWMtYWJjYy03OTVhOWZkYzMyNDkiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJhZGFwdGVwL2xvZ29fc29saXNfaW52ZXJ0LnBuZyIsImlhdCI6MTc1NzUyMTg1NiwiZXhwIjoxNzg5MDU3ODU2fQ.9MI3cgXnovBontRRjAZdc6pGvmt5EK99x_jB6QtZXd0';
      pdf.addImage(solisLogoUrl, 'PNG', pageWidth - 60, pageHeight - 18, 50, 12);
    } catch (error) {
      console.warn('Erro ao carregar logo da Solis:', error);
      // Fallback: texto da Solis
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('SOLIS', pageWidth - 30, pageHeight - 12, { align: 'right' });
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text('LGPD', pageWidth - 30, pageHeight - 8, { align: 'right' });
    }
    
    // Data e hora no canto esquerdo
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.text(moment().format('DD/MM/YYYY HH:mm'), margin, pageHeight - 12);
    
    // Numeração de páginas
    pdf.text(`Página ${i} de ${totalPages}`, pageWidth / 2, pageHeight - 12, { align: 'center' });
    
    // Total de chamados
    pdf.text(`Total de chamados: ${tickets.length}`, margin, pageHeight - 8);
  }
  
  // Baixar arquivo
  pdf.save(fileName);
};

const getOrganizationInfo = (tickets) => {
  if (tickets.length === 0) {
    return { name: null, logoUrl: null };
  }
  
  // Pegar informações da primeira organização encontrada
  const firstTicket = tickets.find(ticket => ticket.org);
  if (!firstTicket?.org) {
    return { name: null, logoUrl: null };
  }
  
  // Validar se a URL da imagem é válida
  let logoUrl = null;
  if (firstTicket.org.imageUrl && 
      typeof firstTicket.org.imageUrl === 'string' && 
      firstTicket.org.imageUrl.startsWith('http')) {
    logoUrl = firstTicket.org.imageUrl;
  }
  
  return {
    name: firstTicket.org.name,
    logoUrl: logoUrl
  };
};

const generateFilterInfo = (filters) => {
  const info = [];

  if (filters.search) {
    info.push(`Filtro de busca: "${filters.search}"`);
  }
  
  if (filters.status) {
    info.push(`Status: ${filters.status}`);
  }
  
  if (filters.priority) {
    info.push(`Prioridade: ${filters.priority}`);
  }
  
  if (filters.dateFrom) {
    info.push(`Data inicial: ${moment(filters.dateFrom).format('DD/MM/YYYY')}`);
  }
  
  if (filters.dateTo) {
    info.push(`Data final: ${moment(filters.dateTo).format('DD/MM/YYYY')}`);
  }

  if (info.length === 0) {
    info.push('Nenhum filtro aplicado (todos os chamados)');
  }

  return info.join('\n');
};

